import { useQueryParams } from "raviger";
import { useTranslation } from "react-i18next";

import CareIcon from "@/CAREUI/icons/CareIcon";
import {
  appendFocusedQuery,
  normalizeFocusedService,
} from "@/components/ui/sidebar/facility/focused-role";

import { NavMain } from "@/components/ui/sidebar/nav-main";

import useCurrentLocation from "@/pages/Facility/locations/utils/useCurrentLocation";
import { CalendarIcon, Logs } from "lucide-react";

export function LocationNav() {
  const { t } = useTranslation();
  const [qParams] = useQueryParams<{ focus?: string }>();

  const { facilityId, locationId } = useCurrentLocation();
  const focus = normalizeFocusedService(qParams.focus);
  const isFocusedMode = focus === "pharmacy" || focus === "inventory";

  const baseUrl = `/facility/${facilityId}/locations/${locationId}`;
  const showPharmacyBranch =
    focus === "pharmacy" ? true : isFocusedMode ? false : true;
  const showInventoryBranch =
    focus === "inventory" ? true : isFocusedMode ? false : true;
  const showLaboratoryBranch = isFocusedMode ? false : true;

  return (
    <NavMain
      links={[
        {
          name: t("beds"),
          url: appendFocusedQuery(`${baseUrl}/beds`, focus),
          icon: <CareIcon icon="l-bed" />,
          visibility: true,
        },
        {
          name: t("laboratory"),
          url: appendFocusedQuery(`${baseUrl}/laboratory`, focus),
          icon: <CareIcon icon="l-microscope" />,
          visibility: showLaboratoryBranch,
          children: [
            {
              name: t("service_requests"),
              url: appendFocusedQuery(`${baseUrl}/service_requests`, focus),
            },
          ],
        },
        {
          name: t("pharmacy"),
          url: appendFocusedQuery(`${baseUrl}/medication_requests`, focus),
          icon: <CareIcon icon="l-medical-drip" />,
          visibility: showPharmacyBranch,
          children: [
            {
              name: t("prescription_queue"),
              url: appendFocusedQuery(`${baseUrl}/medication_requests`, focus),
            },
            {
              name: "\u211E " + t("dispense"),
              url: appendFocusedQuery(`${baseUrl}/medication_dispense`, focus),
            },
            {
              name: t("medication_return"),
              url: appendFocusedQuery(`${baseUrl}/medication_return`, focus),
            },
          ],
        },
        {
          name: t("inventory"),
          url: appendFocusedQuery(`${baseUrl}/inventory/summary`, focus),
          icon: <CareIcon icon="l-shop" />,
          visibility: showInventoryBranch,
          children: [
            {
              name: t("items"),
              url: appendFocusedQuery(`${baseUrl}/inventory/summary`, focus),
            },
            {
              header: t("internal_transfers"),
              name: t("to_receive"),
              url: appendFocusedQuery(
                `${baseUrl}/inventory/internal/receive`,
                focus,
              ),
            },
            {
              name: t("to_dispatch"),
              url: appendFocusedQuery(
                `${baseUrl}/inventory/internal/dispatch`,
                focus,
              ),
            },
            {
              header: t("external_supply"),
              name: t("purchase_orders"),
              url: appendFocusedQuery(
                `${baseUrl}/inventory/external/orders/outgoing`,
                focus,
              ),
            },
            {
              name: t("purchase_deliveries"),
              url: appendFocusedQuery(
                `${baseUrl}/inventory/external/deliveries/incoming`,
                focus,
              ),
            },
          ],
        },
        {
          name: t("schedule"),
          url: appendFocusedQuery(`${baseUrl}/schedule`, focus),
          icon: <CalendarIcon />,
          visibility: true,
        },
        {
          name: t("appointments"),
          url: appendFocusedQuery(`${baseUrl}/appointments`, focus),
          icon: <CareIcon icon="d-calendar" />,
          visibility: true,
        },
        {
          name: t("queues"),
          url: appendFocusedQuery(`${baseUrl}/queues`, focus),
          icon: <Logs />,
          visibility: true,
        },
      ]}
    />
  );
}
